import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { SessionDto } from '@/types/dtos/sessions/session.dto.ts';
import { GET_EVENT_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-event-sessions.ts';
import { GET_EVENT_FILTERED_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-filtered-event-sessions.ts';
import type { AddSessionContributorDto } from '@/types/dtos/sessions/request/add-session-contributor.dto.ts';
import { GET_SESSION_QUERY_KEY } from '@/state/queries/sessions/use-get-session';
import { GET_EVENT_CONTRIBUTORS_QUERY_KEY } from '@/state/queries/contributors/use-get-event-contributors';

export const useAddSessionContributor = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (addSessionContributorDto: AddSessionContributorDto) => {
      const token = await getToken();
      return addSessionContributor(addSessionContributorDto, token ?? '');
    },
    onSuccess: (data, variables) => {
      // Try to get the session data from cache to determine which event it belongs to
      const sessionData = queryClient.getQueryData([GET_SESSION_QUERY_KEY, variables.sessionId]);
      const eventId = (sessionData as SessionDto)?.parent?.id;

      // If we can't determine the event ID from cache, use the returned data
      const finalEventId = eventId || data?.parent?.id;

      return Promise.all(
        [
          // Only invalidate queries for the specific event if we can identify it
          finalEventId && queryClient.invalidateQueries({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY, finalEventId] }),
          finalEventId &&
            queryClient.invalidateQueries({ queryKey: [GET_EVENT_FILTERED_SESSIONS_QUERY_KEY, finalEventId] }),
          queryClient.invalidateQueries({ queryKey: [GET_SESSION_QUERY_KEY, variables.sessionId] }),
          finalEventId && queryClient.invalidateQueries({ queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY, finalEventId] }),

          // Fallback: if we still can't identify the event, invalidate broadly (but this should be rare)
          !finalEventId && queryClient.invalidateQueries({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY] }),
          !finalEventId && queryClient.invalidateQueries({ queryKey: [GET_EVENT_FILTERED_SESSIONS_QUERY_KEY] }),
          !finalEventId && queryClient.invalidateQueries({ queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY] }),
        ].filter(Boolean),
      );
    },
  });
};

const addSessionContributor = async (
  addSessionContributorDto: AddSessionContributorDto,
  token: string,
): Promise<SessionDto> => {
  const { sessionId, ...details } = addSessionContributorDto;
  const url = `${import.meta.env.VITE_API_URL}/sessions/${sessionId}/contributors`;
  return sendJSONRequest({ url, method: METHOD.POST, body: details, token });
};

export default useAddSessionContributor;
